import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Avatar, Drawer, InfoCell, Button } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { getThemeColors } from '../../../lib/theme';
import type { Caregiver } from '../../../types';
import { STATUS_TONE } from './constants';
// WP8: globale Navigation zur neuen BK-Detailseite (/betreuung/{id}) —
// bewusst KEIN neuer Prop-Durchstich durch BetreuungPage/BkAnfragenPage
// (additiver Einbau, beide Verwender profitieren automatisch).
import { buildPath } from '../../../lib/route';
import { CareParamIconRow } from '../../../components/care/CareParamIcons';
import { WiedervorlageBlock } from '../../../components/WiedervorlageBlock';
import type { V1User } from '../../../lib/v1';

interface CaregiverDetailProps {
  item: Caregiver;
  onClose: () => void;
  dark: boolean;
  isMobile?: boolean;
  /** Pflegeparameter-Icon-Keys (CareParamIcons) — vom Aufrufer aus dem Raw-Profil berechnet. */
  iconKeys?: string[];
  /**
   * BK-Anfragen: Roh-User + Save-Callback für den Wiedervorlage-Block
   * (gleiche Komponente wie im PB-Drawer). Ohne Prop (Betreuung-Liste)
   * bleibt der Drawer unverändert.
   */
  wiedervorlage?: { raw: V1User; onSaved: (u: V1User) => void };
}

export function CaregiverDetail({ item, onClose, dark, isMobile = false, iconKeys, wiedervorlage }: CaregiverDetailProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  // WP8: Caregiver.id trägt das Format "BK-{userId}" (v1UserToCaregiver) —
  // numerische User-Id für den Profil-Link extrahieren.
  const bkUserId = Number(String(item.id).replace(/\D+/g, ''));
  return (
    <Drawer open={true} onClose={onClose} width={isMobile ? '100vw' : 520} dark={dark}>
      <div style={{
        padding: isMobile ? '16px 16px' : '20px 24px',
        borderBottom: `1px solid ${t.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{ display: 'flex', gap: isMobile ? 12 : 14, minWidth: 0, flex: 1 }}>
          <Avatar name={item.name} userId={bkUserId || null} size={isMobile ? 44 : 56} tone="brand" dark={dark}/>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: t.muted, fontWeight: 600 }}>{item.id}</div>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: t.heading, marginTop: 2, wordBreak: 'break-word' }}>{item.name}</div>
            <div style={{ fontSize: 12, color: t.muted, marginTop: 3, wordBreak: 'break-word' }}>{item.role} · {item.location}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Badge tone={STATUS_TONE[item.status] as any} dark={dark}>{item.availability}</Badge>
              <Badge tone="warning" dark={dark}>{item.rating} · {item.reviews} {tr('betreuung.detail.reviews')}</Badge>
            </div>
            {/* Pflegeparameter auf einen Blick (Kacheln-PDF) */}
            {(iconKeys?.length ?? 0) > 0 && (
              <div style={{ marginTop: 8 }}>
                <CareParamIconRow keys={iconKeys!} dark={dark} size={17} />
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: isMobile ? 10 : 6, minWidth: isMobile ? 40 : undefined, minHeight: isMobile ? 40 : undefined,
          }}
          aria-label={tr('common.action.close')}
        >
          <Icon.Close c={t.muted} size={isMobile ? 20 : 18}/>
        </button>
      </div>

      <div style={{
        padding: isMobile ? 16 : 24,
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 14 : 18,
      }}>
        <div style={{ background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: 14, fontSize: 13, color: t.text, lineHeight: 1.6 }}>
          {item.bio}
        </div>

        <section>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', marginBottom: 8 }}>{tr('betreuung.detail.section.contact')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <ContactCell dark={dark} label={tr('betreuung.detail.field.phone')} value={item.phone} href={item.phone ? `tel:${item.phone.replace(/\s+/g, '')}` : null} />
            <ContactCell dark={dark} label={tr('betreuung.detail.field.mobile')} value={item.mobile ?? ''} href={item.mobile ? `tel:${item.mobile.replace(/\s+/g, '')}` : null} />
            <ContactCell dark={dark} label={tr('betreuung.detail.field.email')} value={item.email} href={item.email ? `mailto:${item.email}` : null} />
          </div>
        </section>

        {wiedervorlage && (
          <WiedervorlageBlock dark={dark} raw={wiedervorlage.raw} onSaved={wiedervorlage.onSaved} />
        )}

        <section>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', marginBottom: 8 }}>{tr('betreuung.detail.section.qualifications')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {item.certifications.map(c => (
              <div key={c} style={{ padding: '8px 12px', background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 8, fontSize: 13, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon.Check c="#12b76a" size={12}/>{c}
              </div>
            ))}
          </div>
        </section>

        <section>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', marginBottom: 8 }}>{tr('betreuung.detail.section.languages')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {item.languages.map(l => <Badge key={l} tone="info" dark={dark}>{l}</Badge>)}
          </div>
        </section>

        {item.assignments.length > 0 && (
          <section>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', marginBottom: 8 }}>{tr('betreuung.detail.section.assignments')}</div>
            {item.assignments.map(a => (
              <div key={a.id} style={{ padding: 12, background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>{a.client}</div>
                <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>{a.id} · {a.period}</div>
              </div>
            ))}
          </section>
        )}
      </div>

      <div style={{
        padding: isMobile ? 12 : 14,
        borderTop: `1px solid ${t.border}`,
        display: 'flex',
        gap: 8,
        background: t.cardMuted,
        // Mobile: sticky am unteren Drawer-Rand fuer iOS-Safe-Area-Tauglichkeit.
        // Wir zaehlen darauf, dass der scrollbare Body darueber `flex: 1` hat.
        flexShrink: 0,
      }}>
        {/* WP8: Sprung in die volle BK-Detailseite (/betreuung/{id}) */}
        {Number.isFinite(bkUserId) && bkUserId > 0 && (
          <Button
            variant="primary"
            dark={dark}
            style={{ flex: 1, justifyContent: 'center' }}
            href={buildPath('betreuung', String(bkUserId))}
          >{tr('betreuung.detail.action.open_profile')}</Button>
        )}
        {/* Einsatz planen: Sprung auf den Betreuungen-Tab der BK-Detailseite
            (/betreuung/{id}/betreuungen, Tab-Slug s. BkDetailPage BK_TABS). */}
        {Number.isFinite(bkUserId) && bkUserId > 0 && (
          <Button
            variant="primary"
            dark={dark}
            style={{ flex: 1, justifyContent: 'center' }}
            href={buildPath('betreuung', String(bkUserId), 'betreuungen')}
            onClick={() => onClose()}
          >{tr('betreuung.detail.action.plan')}</Button>
        )}
        <Button
          variant="secondary"
          dark={dark}
          style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
        >{tr('betreuung.detail.action.message')}</Button>
      </div>
    </Drawer>
  );
}

/* ─── ContactCell — InfoCell-Optik mit klickbarem tel:/mailto:-Link ───────── */
function ContactCell({ dark, label, value, href }: {
  dark: boolean;
  label: string;
  value: string;
  href: string | null;
}) {
  const t = getThemeColors(dark);
  return (
    <div style={{ background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
      {href && value ? (
        <a href={href} style={{ fontSize: 13, fontWeight: 500, color: '#b5297a', textDecoration: 'none', wordBreak: 'break-all' }}>
          {value}
        </a>
      ) : (
        <div style={{ fontSize: 13, fontWeight: 500, color: t.text }}>{value || '—'}</div>
      )}
    </div>
  );
}
